import type { BannerProps } from "./banner.tsx"

import * as React from "react"

import { useIntersection } from "../../lib/hooks/use-intersection.ts"
import { useIykUser } from "../../lib/hooks/use-iyk-user.ts"
import { Header, type HeaderVariant } from "../../lib/ui/header.tsx"
import { setThemeTo } from "../../lib/utils/classname.ts"
import { Banner } from "./banner.tsx"
import { Footer } from "./footer.tsx"

// #region ProfileLayout

export function ProfileLayout(props: ProfileLayoutProps) {
  return (
    <div>
      <ProfileHeader />
      <main className="min-h-[calc(100dvh-var(--header-height)-var(--footer-height)-6rem)] max-w-screen-2xl mx-auto px-4 md:px-14">
        <div className="md:pt-4">{props.children}</div>
      </main>
      <Footer className="mt-24" />
    </div>
  )
}

type ProfileLayoutProps = {
  children: React.ReactNode
}

// #endregion ProfileLayout

// #region ProfileLayoutWithBanner

export function ProfileLayoutWithBanner(props: ProfileLayoutWithBannerProps) {
  const [isHeaderTransparent, setIsHeaderTransparent] = React.useState(true)
  const bannerRef = useIntersection<HTMLDivElement>({
    onIntersect(entry: IntersectionObserverEntry) {
      setIsHeaderTransparent(entry.isIntersecting)
    },
    options: {
      threshold: 0.15,
    },
  })

  const headerClassName = setThemeTo(isHeaderTransparent ? "dark" : "light")

  return (
    // We use the profile-layout-with-banner and w-screen classes to apply the styles to the body in the globals.css
    // This is to avoid that when a dialog, dropdown or any other Radix UI element (or other library or component)
    // is opened, the scroll bar is blocked and having an element with fixed position will generate a layout shift
    // since it pretends to occupy all the available width of the viewport.
    <div className="profile-layout-with-banner min-h-dvh">
      <ProfileHeader
        variant="fixed"
        className={`${headerClassName} w-screen touch:pr-4 not-touch:pr-8`}
      />
      <Banner {...props.banner} ref={bannerRef} />
      <main className="max-w-screen-2xl mx-auto w-full pl-4 touch:pr-4 not-touch:pr-8 md:pl-12 md:pr-[4rem] pt-16">
        {props.children}
      </main>
      <Footer className="shrink-0 mt-24 touch:pr-4 not-touch:pr-8" />
    </div>
  )
}

type ProfileLayoutWithBannerProps = ProfileLayoutProps & {
  children: React.ReactNode
  banner: BannerProps
}

// #endregion ProfileLayoutWithBanner

// #region ProfileHeader

const ProfileHeader = ({ className, variant }: { className?: string; variant?: HeaderVariant }) => {
  const { isSignedIn, iykUser } = useIykUser()

  return (
    <Header
      variant={variant}
      className={className}
      mainNavigation={{
        kind: "logo",
        navigateTo: isSignedIn ? `/profile/${iykUser.username}` : "",
      }}
    />
  )
}

// #endregion ProfileLayoutHeader
